import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabase';
import { useAuth } from '../contexts/AuthContext';
import { filterEventContent } from '../lib/contentFilter';
import type { EventCategory } from '../types';
import './SubmitEvent.css';

export default function SubmitEvent() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [categories, setCategories] = useState<EventCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [blockError, setBlockError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [location, setLocation] = useState('');
  const [externalUrl, setExternalUrl] = useState('');
  const [categoryId, setCategoryId] = useState('');

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login?redirect=/submit', { replace: true });
      return;
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    async function load() {
      const { data, error: e } = await supabase
        .from('event_categories')
        .select('*')
        .order('name');
      if (e) {
        setError(e.message);
      } else {
        const list = (data as EventCategory[]) ?? [];
        setCategories(list);
        const first = list[0];
        if (first) setCategoryId(first.id);
      }
      setLoading(false);
    }
    load();
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setBlockError(null);
    if (!user) return;

    const filtered = filterEventContent(title, description || null, location || null);
    if (filtered.blocked) {
      setBlockError(filtered.blockReason ?? 'Content not allowed.');
      return;
    }

    const start = startDate || undefined;
    const end = endDate || undefined;
    if (!start || !end) {
      setError('Start date and end date are required.');
      return;
    }
    if (new Date(end) < new Date(start)) {
      setError('End date must be on or after start date.');
      return;
    }
    if (!categoryId) {
      setError('Please select a category.');
      return;
    }

    setSubmitting(true);
    const row = {
      title: filtered.title,
      description: filtered.description || null,
      start_date: start,
      end_date: end,
      location: filtered.location || null,
      external_url: externalUrl.trim() || null,
      category_id: categoryId,
      host_stakeholder_id: null,
      status: 'draft' as const,
      created_by: user.id,
    };
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { error: insertError } = await (supabase.from('events') as any).insert(row);
    setSubmitting(false);

    if (insertError) {
      setError(insertError.message);
      return;
    }
    setSuccess(true);
    setTitle('');
    setDescription('');
    setStartDate('');
    setEndDate('');
    setLocation('');
    setExternalUrl('');
  }

  if (authLoading || (!user && !authLoading)) {
    return (
      <div className="submit-page">
        <p className="submit-loading">Loading…</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="submit-page">
        <div className="submit-success">
          <h2>Thank you</h2>
          <p>Your event has been submitted for review. It will appear on the calendar once approved.</p>
          <button type="button" className="submit-another" onClick={() => setSuccess(false)}>
            Submit another event
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="submit-page">
      <div className="submit-portal">
        <h1>Submit an event</h1>
        <p className="submit-intro">
          Submit a regional event for the calendar. Submissions are reviewed before being published. Profanity and aggressive language are automatically filtered.
        </p>

        {loading ? (
          <p className="submit-loading">Loading categories…</p>
        ) : (
          <form onSubmit={handleSubmit} className="submit-form">
            {(error || blockError) && (
              <div className="submit-error" role="alert">
                {blockError ?? error}
              </div>
            )}
            <label htmlFor="submit-title">Event title *</label>
            <input
              id="submit-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              maxLength={200}
              placeholder="e.g. Regional Music Festival 2025"
            />
            <label htmlFor="submit-desc">Description</label>
            <textarea
              id="submit-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Brief description of the event"
            />
            <div className="submit-row">
              <div className="submit-field">
                <label htmlFor="submit-start">Start date *</label>
                <input
                  id="submit-start"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  required
                />
              </div>
              <div className="submit-field">
                <label htmlFor="submit-end">End date *</label>
                <input
                  id="submit-end"
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  required
                />
              </div>
            </div>
            <label htmlFor="submit-location">Location</label>
            <input
              id="submit-location"
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Venue or town"
            />
            <label htmlFor="submit-url">Event link (optional)</label>
            <input
              id="submit-url"
              type="url"
              value={externalUrl}
              onChange={(e) => setExternalUrl(e.target.value)}
              placeholder="https://..."
            />
            <label htmlFor="submit-category">Category *</label>
            <select
              id="submit-category"
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              required
            >
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
            <button type="submit" className="submit-button" disabled={submitting}>
              {submitting ? 'Submitting…' : 'Submit event'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
